import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, ArrowRight, AlertCircle, Coins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/layout/RequireAuth";
import { EmptyState } from "@/pages/CategoryPage";

export default function Leads() {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState(null);
  const [mustVerify, setMustVerify] = useState(false);
  const complete = user.provider?.profile_complete;

  useEffect(() => {
    refreshUser();
    api.get("/settings/public").then((r) => setMustVerify(r.data.require_provider_verification));
    api.get("/provider/leads").then((r) => setItems(r.data));
  }, [refreshUser]);

  return (
    <div className="container-page max-w-5xl py-10 lg:py-14" data-testid="leads-page">
      <p className="eyebrow">Penyedia</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">Permintaan Masuk</h1>
      <p className="mt-2 text-stone-500">Permintaan terbuka yang sesuai kategori dan area layanan Anda.</p>
      {mustVerify && !user.provider?.verified && (
        <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-5 sm:flex-row sm:items-center" data-testid="verification-required-banner">
          <AlertCircle className="h-5 w-5 shrink-0 text-rose-700" />
          <p className="flex-1 text-sm text-rose-900">
            Tuntas saat ini mewajibkan verifikasi. Selama akun Anda belum terverifikasi, profil dan paket Anda disembunyikan dari publik dan Anda belum dapat mengirim penawaran.
            {user.provider?.verification_status === "menunggu" ? " Dokumen Anda sedang ditinjau." : " Unggah KTP Anda di bagian Verifikasi Akun."}
          </p>
          <Button asChild size="sm" className="rounded-full bg-stone-900 hover:bg-stone-800"><Link to="/dasbor/profil" data-testid="verification-banner-button">Verifikasi sekarang</Link></Button>
        </div>
      )}
      {!complete && (
        <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:flex-row sm:items-center" data-testid="profile-incomplete-alert">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-700" />
          <p className="flex-1 text-sm text-amber-900">Lengkapi kategori layanan dan kota di profil usaha agar permintaan yang sesuai muncul di sini.</p>
          <Button asChild size="sm" className="rounded-full bg-stone-900 hover:bg-stone-800"><Link to="/dasbor/profil" data-testid="complete-profile-button">Lengkapi profil</Link></Button>
        </div>
      )}
      <div className="mt-6 flex items-center gap-2 text-sm text-stone-600">
        <Coins className="h-4 w-4 text-amber-600" /> Saldo: <b data-testid="leads-credit-balance">{user.provider?.credits ?? 0} kredit</b> · 1 kredit per penawaran
      </div>
      <div className="mt-6 space-y-4">
        {items === null ? <PageLoader /> : items.length === 0 ? (
          <EmptyState testId="leads-empty" title="Belum ada permintaan baru" desc="Permintaan baru di kategori dan kota Anda akan muncul di sini." />
        ) : items.map((r) => (
          <Link key={r.request_id} to={`/dasbor/permintaan-masuk/${r.request_id}`} data-testid={`lead-card-${r.request_id}`}
            className="card-soft card-lift flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-bold text-stone-900">{r.category_name}</h2>
                <span className="chip">{r.quote_count} penawaran</span>
              </div>
              <p className="mt-1 line-clamp-2 text-sm text-stone-500">{r.description}</p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-500">
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {r.city_name}</span>
                <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {formatDate(r.preferred_date)}</span>
                <span>oleh {r.customer_name?.split(" ")[0]}</span>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700">Lihat & kirim penawaran <ArrowRight className="h-4 w-4" /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}
